// Self-contained client for the device-config-service (fleet boot/loading branding).
// Mirrors issuesApi.ts conventions.

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveDeviceConfigHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/device-config-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://device-config.api.${apex}`;
  if (stage === 'staging') return `https://device-config.api.stage.${apex}`;
  return `https://device-config.api.dev.${apex}`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_DEVICE_CONFIG_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveDeviceConfigHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) throw new Error(`device-config ${res.status}: ${await res.text().catch(() => '')}`);
  return res.status === 204 ? (undefined as T) : res.json();
}

export type BrandingSlot = 'boot' | 'loading';

export const deviceConfigApi = {
  listDevices: () => call<{ devices: any[]; cdnDomain?: string }>(`/devices`),
  presignUpload: (deviceType: string, slot: BrandingSlot, mime: string, bytes: number) =>
    call<{ uploadId: string; key: string; url: string }>(`/devices/${deviceType}/branding/${slot}/presign-upload`, { method: 'POST', body: JSON.stringify({ mime, bytes }) }),
  /**
   * `mime` MUST be the same string presignUpload was given. S3 signs the
   * Content-Type into the presigned PUT, so sending a different one — or
   * sending the browser's empty `file.type` for a .zip after presigning as
   * application/zip — fails the signature check with a 403 that reads like a
   * permissions problem. Defaults to the file's own type for every existing
   * caller.
   */
  putToPresigned: async (url: string, file: File, mime?: string) => {
    const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': mime || file.type }, body: file });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  },
  publish: (deviceType: string) => call<any>(`/devices/${deviceType}/publish`, { method: 'POST' }),
  promote: (deviceType: string) => call<any>(`/devices/${deviceType}/promote`, { method: 'POST' }),
  revert: (deviceType: string, slot: BrandingSlot, version: number) =>
    call<any>(`/devices/${deviceType}/branding/${slot}/revert`, { method: 'POST', body: JSON.stringify({ version }) }),
  /**
   * Every `device#` row for a type. The handler follows `LastEvaluatedKey`, so
   * this is the WHOLE partition unless it says `truncated: true` — surface that
   * flag, never infer completeness from the row count.
   */
  listDeviceStatus: (deviceType: string) =>
    call<{ devices: any[]; summary: any; truncated?: boolean }>(`/devices/${deviceType}/status`),
  /**
   * Phase C release ledger — one device's software-version history, newest
   * first. Super-admin gated; `limit` defaults to 50 server-side and is capped
   * at 200. Rows can contain duplicate adjacent transitions (accepted Phase C
   * limit) — run the result through `dedupeLedgerEntries` from
   * `pages/pos/fleet/fleetRows` before rendering.
   */
  listDeviceLedger: (deviceType: string, deviceId: string, limit?: number) =>
    call<{ entries: any[] }>(
      `/devices/${deviceType}/devices/${deviceId}/ledger${limit ? `?limit=${limit}` : ''}`,
    ),
  /**
   * Task D6 — what the SERVER resolved each device should be running, for the
   * Fleet table's Target/drift columns. Super-admin gated (401 unauthenticated).
   *
   * The handler PAGINATES to the end of the device partition, matching the
   * `/status` read, so the two halves of a fleet row can never disagree about
   * which devices exist. `limit` is an optional overall ceiling, not a page
   * size; when a ceiling stops the read short the response carries
   * `truncated: true` and the caller MUST say so — a missing target must never
   * be mistaken for "the server resolved nothing".
   *
   * A target must never be inferred client-side from a pin: the server falls
   * back from a stale pin to the device's ring, so a client-side guess would
   * disagree with what the device actually receives on check-in. `target` is
   * `null` for a device the server could resolve nothing for — feed the result
   * through `resolvedTargetMapFromEntries` (pages/pos/fleet/fleetRows), which
   * drops those so the row reads "unknown" instead of a fabricated target.
   */
  listResolvedTargets: (deviceType: string, limit?: number) =>
    call<{
      deviceType: string;
      targets: Array<{
        deviceId: string;
        target: { version?: string; url?: string; hash?: string; source?: 'pin' | 'ring' } | null;
      }>;
      count: number;
      truncated?: boolean;
      limit?: number;
    }>(`/devices/${deviceType}/resolved-targets${limit ? `?limit=${limit}` : ''}`),
  setDeviceChannel: (deviceType: string, deviceId: string, channel: 'canary' | 'stable') =>
    call<any>(`/devices/${deviceType}/devices/${deviceId}/channel`, { method: 'POST', body: JSON.stringify({ channel }) }),
  /**
   * `PUT /devices/{deviceType}/devices/{deviceId}/release` — Phase B's ring/pin
   * setter (`setDeviceRelease.ts` on device-config-service; serverless.yml
   * declares this route `method: put`, NOT post — confirmed against
   * `services/agenticpos/device-config-service/serverless.yml`). Send only the
   * key(s) you mean to change: `releaseRing` moves the ring, `pinnedVersion`
   * sets or clears the pin. `undefined` and `null` are NOT interchangeable —
   * `JSON.stringify` drops an `undefined` key entirely, so a caller that means
   * "leave the ring alone, clear the pin" must send `{ pinnedVersion: null }`,
   * never `{ pinnedVersion: undefined }`: an omitted key survives on the
   * server as whatever pin was already stored.
   */
  setDeviceRelease: (deviceType: string, deviceId: string, body: { releaseRing?: 'canary' | 'pilot' | 'stable'; pinnedVersion?: string | null }) =>
    call<{ deviceId: string; releaseRing?: string; pinnedVersion?: string | null }>(
      `/devices/${deviceType}/devices/${deviceId}/release`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  /**
   * `PUT /devices/{deviceType}/devices/{deviceId}/auto-update` — the remote
   * off switch (Task 10). `setDeviceAutoUpdate.ts` on device-config-service;
   * serverless.yml declares this route `method: put`, matching this call —
   * a client POSTing to a PUT-only route is exactly the bug this file's
   * `setDeviceRelease` note above exists to prevent, so be deliberate here too.
   * The flag is stored on the device row and handed back on the device's next
   * check-in, never delivered as a command — see `CheckinResponseSchema.autoUpdate`.
   */
  setDeviceAutoUpdate: (deviceType: string, deviceId: string, autoUpdate: boolean) =>
    call<{ deviceId: string; autoUpdate: boolean }>(
      `/devices/${deviceType}/devices/${deviceId}/auto-update`,
      { method: 'PUT', body: JSON.stringify({ autoUpdate }) },
    ),
  deleteSlot: (deviceType: string, slot: BrandingSlot) =>
    call<any>(`/devices/${deviceType}/branding/${slot}`, { method: 'DELETE' }),
  /**
   * Remove one device's check-in row from the fleet.
   *
   * A terminal that is still running the POS app re-registers on its next poll
   * (check-in is an unauthenticated upsert), so this clears a record — it does
   * not decommission hardware. `previous` carries the release state that was
   * discarded, including any pin.
   */
  deleteDevice: (deviceType: string, deviceId: string) =>
    call<{ deviceId: string; removed: boolean; previous: Record<string, unknown> }>(
      `/devices/${encodeURIComponent(deviceType)}/devices/${encodeURIComponent(deviceId)}`,
      { method: 'DELETE' },
    ),
};
